(function (scope, factory) {
  var api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (scope && scope.document) {
    scope.SFPrescriptionHealth = api;
    api.mountAll(scope.document);
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var REGION_ORDER = ['legs', 'arms', 'hips'];
  var REGION_LABELS = {
    legs: 'Beine',
    arms: 'Arme',
    hips: 'Hüfte & Becken'
  };
  var STEP_ORDER = ['safety', 'regions', 'context', 'monitor', 'result'];

  function normalizeRegions(regions) {
    var values = Array.isArray(regions) ? regions : [];
    return REGION_ORDER.filter(function (region) {
      return values.indexOf(region) !== -1;
    });
  }

  function createState() {
    return {
      safety: null,
      regions: [],
      context: {},
      monitor: null,
      blocked: null
    };
  }

  function evaluateSafety(value) {
    var routes = {
      emergency: {
        source: 'safety',
        level: 'emergency',
        title: 'Bitte jetzt medizinische Hilfe holen.',
        summary: 'Brustschmerz, Atemnot, Ohnmacht oder sehr starke beziehungsweise rasch zunehmende akute Beschwerden können ein Notfall sein. Der Produktpfad endet hier.',
        action: 'In Deutschland 112 anrufen. Nicht auf eine Online-Orientierung oder Produktentscheidung warten.',
        help: ['112', 'official-emergency']
      },
      unilateral: {
        source: 'safety',
        level: 'same-day',
        title: 'Bitte die neue einseitige Veränderung heute abklären.',
        summary: 'Neu einseitige Schwellung, Schmerz, Wärme oder Rötung lässt sich online nicht sicher einordnen. Deshalb zeigen wir keine Produktkonfiguration.',
        action: 'Während der Sprechzeit die hausärztliche Praxis kontaktieren; außerhalb regulärer Sprechzeiten hilft 116117. Bei Atemnot, Brustschmerz, Ohnmacht oder rascher Verschlechterung 112.',
        help: ['116117', '112', 'official-116117', 'official-dvt']
      },
      skin: {
        source: 'safety',
        level: 'same-day',
        title: 'Bitte Haut und Allgemeinzustand zuerst abklären.',
        summary: 'Offene oder nässende Stellen sowie deutlich rote oder heiße Haut mit Fieber gehören fachlich beurteilt. Der Produktpfad endet hier.',
        action: 'Während der Sprechzeit die hausärztliche Praxis kontaktieren; außerhalb regulärer Sprechzeiten hilft 116117. Bei schweren akuten Zeichen 112.',
        help: ['116117', '112', 'official-116117', 'official-skin', 'manual']
      },
      unsure: {
        source: 'safety',
        level: 'clarify',
        title: 'Bitte vor einer Produktauswahl fachlich klären.',
        summary: 'Bei Unsicherheit trifft dieser Flow bewusst keine technische Zuordnung.',
        action: 'Für dringende, nicht lebensbedrohliche Beschwerden ist 116117 erreichbar; bei Lebensgefahr 112.',
        help: ['116117', '112', 'official-116117', 'official-emergency']
      }
    };

    return routes[value] || null;
  }

  function evaluateContext(context, regions) {
    var selected = normalizeRegions(regions);
    var answers = context || {};
    var careRegion = selected.find(function (region) {
      return answers[region] === 'care';
    });
    var persistentRegion = selected.find(function (region) {
      return answers[region] === 'persistent';
    });

    if (careRegion) {
      return {
        source: 'context',
        level: 'care-team',
        title: 'Bitte die Anwendung mit deinem Fachteam abstimmen.',
        summary: 'Für ' + REGION_LABELS[careRegion] + ' hast du eine bestehende Diagnose, verordnete Kompression oder laufende Behandlung angegeben. Eine allgemeine Produktauswahl ersetzt diese individuelle Abstimmung nicht.',
        action: 'Behandelnde Praxis oder Therapie-Team zur konkreten Anwendung und zu den Hinweisen der Produktanleitung befragen.',
        help: ['manual']
      };
    }

    if (persistentRegion) {
      return {
        source: 'context',
        level: 'clarify',
        title: 'Bitte anhaltende oder unklare Beschwerden zuerst abklären.',
        summary: 'Für ' + REGION_LABELS[persistentRegion] + ' hast du neue, anhaltende, zunehmende oder unklare Beschwerden angegeben. Deshalb folgt keine Produktkonfiguration.',
        action: 'Hausärztliche Praxis kontaktieren; außerhalb der Sprechzeiten hilft 116117 bei dringenden, nicht lebensbedrohlichen Beschwerden weiter.',
        help: ['116117', 'official-116117', 'manual']
      };
    }

    return null;
  }

  function needsMonitorStep(regions) {
    var selected = normalizeRegions(regions);
    var hasAccessory = selected.indexOf('arms') !== -1 || selected.indexOf('hips') !== -1;
    return hasAccessory && selected.indexOf('legs') === -1;
  }

  function completeContext(context, regions) {
    var answers = context || {};
    return normalizeRegions(regions).every(function (region) {
      return ['routine', 'coverage', 'persistent', 'care'].indexOf(answers[region]) !== -1;
    });
  }

  function buildOrientation(regions, monitor, products) {
    var selected = normalizeRegions(regions);
    var names = {
      boots: 'STARKBOOTS V2',
      sleeves: 'STARKSLEEVES V2',
      hips: 'STARKHIPS V2',
      kit: 'Ultimate Recovery Kit'
    };
    Object.keys(names).forEach(function (key) {
      if (products && typeof products[key] === 'string' && products[key].trim()) names[key] = products[key].trim();
    });
    var hasLegs = selected.indexOf('legs') !== -1;
    var hasArms = selected.indexOf('arms') !== -1;
    var hasHips = selected.indexOf('hips') !== -1;
    var result = {
      name: '',
      products: [],
      monitorNote: '',
      regionsText: selected.map(function (region) { return REGION_LABELS[region]; }).join(', '),
      monitorText: hasLegs ? 'Über die Basiseinheit im Boots-System abgedeckt' : '',
      summary: ''
    };

    if (hasLegs && hasArms && hasHips) {
      result.name = 'Komplettes Drei-Bereiche-System';
      result.products = [names.kit];
      result.monitorNote = 'Das Komplett-Kit bündelt Boots, Arm- und Hüft-Erweiterung mit der benötigten Basiseinheit.';
    } else if (hasLegs) {
      result.name = hasArms || hasHips ? 'Boots-Basis mit Erweiterung' : 'Boots-Basissystem';
      result.products = [names.boots];
      if (hasArms) result.products.push(names.sleeves);
      if (hasHips) result.products.push(names.hips);
      result.monitorNote = hasArms || hasHips
        ? 'Das Boots-System stellt die Basiseinheit; die ausgewählten Sleeves/Hips bleiben transparente Zusatzteile.'
        : 'Das Boots-System enthält die für diesen Bereich benötigte Basiseinheit.';
    } else if (monitor === 'yes') {
      result.name = hasArms && hasHips ? 'Zwei Erweiterungen für vorhandene Basis' : 'Erweiterung für vorhandene Basis';
      if (hasArms) result.products.push(names.sleeves);
      if (hasHips) result.products.push(names.hips);
      result.monitorText = 'Kompatibler Smart Monitor laut Angabe vorhanden';
      result.monitorNote = 'Sleeves und Hips sind Erweiterungen ohne eigenes Steuergerät. Die Zuordnung setzt den angegebenen kompatiblen Smart Monitor voraus.';
    } else if (monitor === 'no') {
      if (hasArms && hasHips) {
        result.name = 'Komplett-Kit inklusive Basiseinheit';
        result.products = [names.kit];
        result.monitorNote = 'Ohne vorhandenen Smart Monitor wird die Basiseinheit benötigt. Das Komplett-Kit enthält zusätzlich die Boots für die Beine; dieser Mehrumfang wird bewusst offengelegt.';
      } else {
        result.name = 'Basissystem plus Erweiterung';
        result.products = [names.boots, hasArms ? names.sleeves : names.hips];
        result.monitorNote = 'Die Erweiterung enthält kein eigenes Steuergerät. Deshalb kommt das Boots-Basissystem für den Smart Monitor hinzu, auch wenn die Beine nicht als Zielbereich gewählt wurden.';
      }
      result.monitorText = 'Noch kein Smart Monitor vorhanden';
    } else {
      result.name = hasArms && hasHips ? 'Erweiterungen – Basis vorab prüfen' : 'Erweiterung – Basis vorab prüfen';
      if (hasArms) result.products.push(names.sleeves);
      if (hasHips) result.products.push(names.hips);
      result.monitorText = 'Kompatibilität noch ungeklärt';
      result.monitorNote = 'Sleeves und Hips funktionieren nicht allein. Vor dem Kauf muss geklärt werden, ob ein kompatibler Smart Monitor vorhanden ist; andernfalls wird zusätzlich eine Boots-Basis oder das Komplett-Kit benötigt.';
    }

    result.summary = 'Aus ' + result.regionsText + ' ergibt sich eine technische Set-Orientierung. Beschwerden, Diagnose, Druckstufe und persönliche Eignung werden dabei nicht bewertet.';
    return result;
  }

  function createNavigator(start) {
    var current = start || 'safety';
    var history = [];

    return {
      current: function () { return current; },
      trail: function () { return history.slice(); },
      visit: function (step) {
        if (step !== current) {
          history.push(current);
          current = step;
        }
        return current;
      },
      back: function () {
        if (history.length) current = history.pop();
        return current;
      },
      reset: function (step, trail) {
        current = step || 'safety';
        history = Array.isArray(trail) ? trail.slice() : [];
        return current;
      }
    };
  }

  function parseConfig(root) {
    var node = root.querySelector('[data-sfh3-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function setText(node, value) {
    if (node) node.textContent = value || '';
  }

  function mount(root) {
    if (!root || root.getAttribute('data-sfh3-ready') === 'true') return null;
    root.setAttribute('data-sfh3-ready', 'true');

    var config = parseConfig(root);
    var state = createState();
    var navigator = createNavigator('safety');
    var status = root.querySelector('[data-sfh3-status]');
    var progressItems = Array.prototype.slice.call(root.querySelectorAll('[data-sfh3-progress]'));
    var backButton = root.querySelector('.sfph__topbar [data-sfh3-back]');
    var contextMount = root.querySelector('[data-sfh3-context-mount]');
    var reduceMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    function showError(key, show) {
      var node = root.querySelector('[data-sfh3-error="' + key + '"]');
      if (node) node.hidden = !show;
    }

    function announce(message) {
      if (!status) return;
      status.textContent = '';
      requestAnimationFrame(function () { status.textContent = message; });
    }

    function focusStep(step) {
      var panel = root.querySelector('[data-sfh3-step="' + step + '"]');
      var heading = panel && panel.querySelector('h3[tabindex="-1"]');
      if (!heading) return;
      requestAnimationFrame(function () {
        heading.focus({ preventScroll: true });
        var bounds = root.getBoundingClientRect();
        if (!reduceMotion && (bounds.top < -24 || bounds.top > window.innerHeight - 80)) {
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    function updateProgress(step) {
      var displayStep = step === 'blocked' ? (state.blocked && state.blocked.source === 'context' ? 'context' : 'safety') : step;
      var currentIndex = STEP_ORDER.indexOf(displayStep);
      progressItems.forEach(function (item) {
        var index = STEP_ORDER.indexOf(item.getAttribute('data-sfh3-progress'));
        var current = index === currentIndex;
        item.classList.toggle('is-current', current);
        item.classList.toggle('is-complete', step !== 'blocked' && index < currentIndex);
        if (current) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }

    function showStep(step, options) {
      var settings = options || {};
      if (settings.push !== false) navigator.visit(step);
      Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-step]'), function (panel) {
        var active = panel.getAttribute('data-sfh3-step') === step;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      updateProgress(step);
      if (backButton) backButton.hidden = navigator.trail().length === 0 || step === 'blocked';
      announce(settings.message || 'Schritt gewechselt.');
      focusStep(step);
    }

    function updateRegions() {
      state.regions = normalizeRegions(state.regions);
      Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-region-toggle]'), function (button) {
        var selected = state.regions.indexOf(button.getAttribute('data-sfh3-region-toggle')) !== -1;
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.classList.toggle('is-selected', selected);
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-body-region]'), function (shape) {
        shape.classList.toggle('is-selected', state.regions.indexOf(shape.getAttribute('data-sfh3-body-region')) !== -1);
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-region-label]'), function (label) {
        label.classList.toggle('is-selected', state.regions.indexOf(label.getAttribute('data-sfh3-region-label')) !== -1);
      });

      var summary = root.querySelector('[data-sfh3-region-summary]');
      if (summary) {
        var strong = summary.querySelector('strong');
        var detail = summary.querySelector('span');
        if (state.regions.length) {
          setText(strong, state.regions.map(function (region) { return REGION_LABELS[region]; }).join(', '));
          setText(detail, state.regions.length === 1 ? 'Ein Bereich ausgewählt.' : state.regions.length + ' Bereiche ausgewählt.');
        } else {
          setText(strong, 'Noch kein Bereich gewählt.');
          setText(detail, 'Mehrfachauswahl ist möglich.');
        }
      }

      var continueButton = root.querySelector('[data-sfh3-continue="regions"]');
      if (continueButton) continueButton.disabled = state.regions.length === 0;
      showError('regions', false);
    }

    function renderContext() {
      if (!contextMount) return;
      contextMount.textContent = '';
      state.regions.forEach(function (region) {
        var template = root.querySelector('[data-sfh3-context-template="' + region + '"]');
        if (!template || !template.content) return;
        var fragment = template.content.cloneNode(true);
        var fieldset = fragment.querySelector('[data-sfh3-context-region]');
        Array.prototype.forEach.call(fragment.querySelectorAll('input[type="radio"]'), function (input) {
          input.name = 'sfph-context-' + (config.sectionId || 'section') + '-' + region;
          input.checked = state.context[region] === input.value;
        });
        if (fieldset) fieldset.setAttribute('data-sfh3-context-region', region);
        contextMount.appendChild(fragment);
      });
      updateContextContinue();
    }

    function updateContextContinue() {
      var button = root.querySelector('[data-sfh3-continue="context"]');
      if (button) button.disabled = !completeContext(state.context, state.regions);
    }

    function showBlocked(block) {
      state.blocked = block;
      setText(root.querySelector('[data-sfh3-blocked-title]'), block.title);
      setText(root.querySelector('[data-sfh3-blocked-summary]'), block.summary);
      setText(root.querySelector('[data-sfh3-blocked-action]'), block.action);
      Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-help]'), function (link) {
        link.hidden = block.help.indexOf(link.getAttribute('data-sfh3-help')) === -1;
      });
      showStep('blocked', { message: 'Sicherheitsstop. Es wird keine Produktkonfiguration gezeigt.' });
    }

    function renderResult() {
      var result = buildOrientation(state.regions, state.monitor, config.products);
      setText(root.querySelector('[data-sfh3-result-summary]'), result.summary);
      setText(root.querySelector('[data-sfh3-orientation-name]'), result.name);
      setText(root.querySelector('[data-sfh3-monitor-note]'), result.monitorNote);
      setText(root.querySelector('[data-sfh3-result-regions]'), result.regionsText);
      setText(root.querySelector('[data-sfh3-result-monitor]'), result.monitorText);

      var list = root.querySelector('[data-sfh3-product-list]');
      if (list) {
        list.textContent = '';
        result.products.forEach(function (product, index) {
          var item = document.createElement('li');
          var marker = document.createElement('span');
          var label = document.createElement('strong');
          marker.textContent = String(index + 1).padStart(2, '0');
          label.textContent = product;
          item.appendChild(marker);
          item.appendChild(label);
          list.appendChild(item);
        });
      }
    }

    function restart() {
      state = createState();
      navigator.reset('safety');
      Array.prototype.forEach.call(root.querySelectorAll('input[type="radio"]'), function (input) { input.checked = false; });
      Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-error]'), function (error) { error.hidden = true; });
      var dryRunStatus = root.querySelector('[data-sfh3-dry-run-status]');
      if (dryRunStatus) {
        dryRunStatus.hidden = true;
        dryRunStatus.textContent = '';
      }
      if (contextMount) contextMount.textContent = '';
      updateRegions();
      showStep('safety', { push: false, message: 'Orientierung neu gestartet. Alle Angaben wurden aus dem Arbeitsspeicher entfernt.' });
    }

    root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || input.tagName !== 'INPUT') return;
      var safetyGroup = input.closest('[data-sfh3-safety-group]');
      var monitorGroup = input.closest('[data-sfh3-monitor-group]');
      var contextGroup = input.closest('[data-sfh3-context-region]');
      if (safetyGroup) {
        state.safety = input.value;
        showError('safety', false);
      } else if (monitorGroup) {
        state.monitor = input.value;
        showError('monitor', false);
      } else if (contextGroup) {
        state.context[contextGroup.getAttribute('data-sfh3-context-region')] = input.value;
        showError('context', false);
        updateContextContinue();
      }
    });

    root.addEventListener('click', function (event) {
      var regionButton = event.target.closest('[data-sfh3-region-toggle]');
      var continueButton = event.target.closest('[data-sfh3-continue]');
      var back = event.target.closest('[data-sfh3-back]');
      var restartButton = event.target.closest('[data-sfh3-restart]');
      var editButton = event.target.closest('[data-sfh3-edit]');
      var dryRun = event.target.closest('[data-sfh3-dry-run]');

      if (regionButton) {
        var region = regionButton.getAttribute('data-sfh3-region-toggle');
        var index = state.regions.indexOf(region);
        if (index === -1) state.regions.push(region);
        else state.regions.splice(index, 1);
        state.monitor = null;
        Array.prototype.forEach.call(root.querySelectorAll('[data-sfh3-monitor-group] input'), function (input) { input.checked = false; });
        Object.keys(state.context).forEach(function (key) {
          if (state.regions.indexOf(key) === -1) delete state.context[key];
        });
        updateRegions();
        return;
      }

      if (continueButton) {
        var from = continueButton.getAttribute('data-sfh3-continue');
        if (from === 'safety') {
          if (!state.safety) return showError('safety', true);
          var safetyBlock = evaluateSafety(state.safety);
          if (safetyBlock) return showBlocked(safetyBlock);
          showStep('regions', { message: 'Sicherheitscheck abgeschlossen. Körperbereich auswählen.' });
        } else if (from === 'regions') {
          if (!state.regions.length) return showError('regions', true);
          renderContext();
          showStep('context', { message: 'Bereich gewählt. Kontextfragen beantworten.' });
        } else if (from === 'context') {
          if (!completeContext(state.context, state.regions)) return showError('context', true);
          var contextBlock = evaluateContext(state.context, state.regions);
          if (contextBlock) return showBlocked(contextBlock);
          if (needsMonitorStep(state.regions)) {
            showStep('monitor', { message: 'Kontext abgeschlossen. Vorhandene Basiseinheit angeben.' });
          } else {
            state.monitor = 'included';
            renderResult();
            showStep('result', { message: 'Technische Orientierung verfügbar.' });
          }
        } else if (from === 'monitor') {
          if (!state.monitor) return showError('monitor', true);
          renderResult();
          showStep('result', { message: 'Technische Orientierung verfügbar.' });
        }
        return;
      }

      if (back) {
        var previous = navigator.back();
        if (previous === 'context') renderContext();
        showStep(previous, { push: false, message: 'Zur vorherigen Frage zurückgekehrt.' });
        return;
      }

      if (restartButton) {
        restart();
        return;
      }

      if (editButton) {
        if (state.blocked && state.blocked.source === 'safety') {
          navigator.reset('safety');
          showStep('safety', { push: false, message: 'Sicherheitsangabe kann geändert werden.' });
        } else {
          navigator.reset('context', ['safety', 'regions']);
          renderContext();
          showStep('context', { push: false, message: 'Antworten können geändert werden.' });
        }
        state.blocked = null;
        return;
      }

      if (dryRun) {
        var dryStatus = root.querySelector('[data-sfh3-dry-run-status]');
        if (dryStatus) {
          dryStatus.hidden = false;
          dryStatus.textContent = 'Dry-run bestätigt: vorschau@example.com blieb lokal; kein Versand, Lead oder Speichereintrag wurde ausgelöst.';
        }
      }
    });

    if (typeof IntersectionObserver === 'function') {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          root.classList.toggle('is-offscreen', !entry.isIntersecting);
        });
      }, { threshold: 0.02 });
      observer.observe(root);
    }

    updateRegions();
    updateProgress('safety');
    return {
      getState: function () {
        return {
          safety: state.safety,
          regions: state.regions.slice(),
          context: Object.assign({}, state.context),
          monitor: state.monitor,
          blocked: state.blocked ? Object.assign({}, state.blocked) : null,
          step: navigator.current()
        };
      },
      restart: restart
    };
  }

  function mountAll(documentNode) {
    function run() {
      Array.prototype.forEach.call(documentNode.querySelectorAll('[data-sfh3-prescription-health]'), mount);
    }
    if (documentNode.readyState === 'loading') documentNode.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
  }

  return {
    REGION_ORDER: REGION_ORDER,
    REGION_LABELS: REGION_LABELS,
    normalizeRegions: normalizeRegions,
    createState: createState,
    evaluateSafety: evaluateSafety,
    evaluateContext: evaluateContext,
    needsMonitorStep: needsMonitorStep,
    completeContext: completeContext,
    buildOrientation: buildOrientation,
    createNavigator: createNavigator,
    mount: mount,
    mountAll: mountAll
  };
});
